// Izobraževalno-prodajni članek za trgovce na kupish.si.
// Tema: kako napisati opis izdelka, ki prodaja.

export const opisIzdelkaArticle = {
  slug: "kako-napisati-opis-izdelka",
  title: "Kako napisati opis izdelka, ki prodaja",
  section: "unikatne-vsebine" as const,
  date: "2026-09-15 08:00:00",
  excerpt:
    "Praktičen vodnik za trgovce: kako sestaviti opis izdelka, ki odgovori na kupčeva vprašanja, zdrži primerjavo s konkurenco in se uvrsti v Googlu. S strukturo, primeri, pogostimi napakami in kontrolnim seznamom.",
  content: `
<p><strong>Opis izdelka</strong> je prodajalec, ki dela 24 ur na dan. Kupec ga prebere namesto pogovora z vami, na njegovi podlagi se odloči za nakup — ali pa zapusti stran in kupi drugje. V spletni trgovini je opis edino mesto, kjer lahko pojasnite, zakaj je vaš izdelek vreden svoje cene.</p>

<p>Večina opisov v slovenskih spletnih trgovinah je prepisana od dobavitelja. To pomeni dvoje: kupec ne dobi nobenega novega podatka, Google pa vidi enako besedilo na desetih straneh in nobene od njih ne postavi visoko. Ta vodnik pojasni, kako napisati opis, ki dejansko opravi svoje delo.</p>

<h2>Kaj opis izdelka sploh mora doseči</h2>

<p>Preden se lotite pisanja, je koristno vedeti, kaj mora besedilo doseči. Dober opis opravi štiri naloge hkrati:</p>

<ul>
<li><strong>Odgovori na kupčeva vprašanja.</strong> Kakšna je velikost, iz česa je, koliko časa zdrži, komu ustreza, kaj je v paketu.</li>
<li><strong>Odpravi dvom.</strong> Kupec ne more izdelka prijeti v roke, zato mora besedilo nadomestiti občutek. Kaj bi preveril, če bi stal v trgovini?</li>
<li><strong>Pojasni razliko v ceni.</strong> Če ste dražji od konkurence, mora opis povedati, zakaj.</li>
<li><strong>Pomaga Googlu razumeti stran.</strong> Naravna raba izrazov, ki jih kupci dejansko iščejo.</li>
</ul>

<p>Če opis ne opravi vsaj prvih treh nalog, ga kupec prebere in odide. Četrta naloga sama po sebi ne proda ničesar — le pripelje obiskovalca do strani, na kateri prve tri opravijo svoje delo.</p>

<h2>Kako kupec bere opis izdelka</h2>

<p>Kupec ne bere. Kupec skenira. V prvih nekaj sekundah pogled preleti sliko, ceno, naslov in prve vrstice besedila. Šele če ga tam nekaj zadrži, začne brati podrobneje.</p>

<p>Iz tega sledi zelo praktično pravilo: <strong>najpomembnejši podatek sodi na vrh</strong>. Ne v tretji odstavek, ne pod zavihek s tehničnimi podrobnostmi. Če prodajate zimsko jakno, mora biti v prvih dveh vrsticah jasno, do katere temperature greje in ali je vodoodporna. Vse ostalo lahko počaka.</p>

<p>Drugo pravilo je vizualna prebavljivost. Trije kratki odstavki in seznam alinej se preberejo; blok dvajsetih vrstic se preskoči. Uporabljajte kratke povedi, podnaslove in alineje — ne zato, ker je moderno, ampak ker tako kupec dejansko najde tisto, kar išče.</p>

<h2>Struktura opisa, ki deluje</h2>

<p>Naslednja struktura je preprosta, ponovljiva in primerna za praktično vsak izdelek. Uporabite jo kot okvir, ne kot obrazec.</p>

<h3>1. Uvodni stavek: kaj je in za koga je</h3>

<p>Ena ali dve povedi, ki povesta bistvo. Ne uvoda o zgodovini podjetja, ne splošnih fraz o kakovosti. Kupec mora v desetih besedah vedeti, ali je na pravi strani.</p>

<p>Slabo: »Ta izdelek je rezultat dolgoletnih izkušenj in vrhunske kakovosti.«<br>
Dobro: »Termo steklenica za 0,5 litra, ki ohrani napitek vroč 12 ur — za pisarno, hribe in vožnjo v službo.«</p>

<h3>2. Ključne lastnosti v alinejah</h3>

<p>Tri do šest alinej z najpomembnejšimi podatki. Vsaka alineja naj bo lastnost plus korist: ne le »dvojna stena«, ampak »dvojna stena — zunanjost se ne segreje, steklenico lahko držite v roki«.</p>

<p>To je najbolj branemu delu opisa najbližje. Če boste popravljali samo eno stvar, popravite alineje.</p>

<h3>3. Podrobnejši opis</h3>

<p>Dva do štirje odstavki, v katerih razložite, kako se izdelek obnese v resnični uporabi. Tu je prostor za vaše znanje: kaj kupci najpogosteje sprašujejo, na kaj naj pazijo, s čim se izdelek dobro kombinira.</p>

<p>Če izdelek prodajate že dlje časa, imate to znanje v elektronski pošti. Preberite zadnjih dvajset vprašanj kupcev — to je najboljša surovina za ta del opisa.</p>

<h3>4. Tehnični podatki</h3>

<p>Mere, teža, material, vsebina paketa, garancija, izvor. Ta del naj bo strukturiran in dolgočasen — natanko tak, kakršnega kupec potrebuje za primerjavo. Na kupish.si so tehnične podrobnosti prikazane v ločenem zavihku, tako da glavni opis ostane pregleden.</p>

<h3>5. Odgovor na dvom</h3>

<p>Vsak izdelek ima eno vprašanje, ki kupca najpogosteje ustavi: »Ali mi bo prav?«, »Ali bo dovolj močno?«, »Kaj če ne ustreza?«. Napišite odgovor nanj, preden ga kupec postavi. Ta odstavek pogosto naredi razliko med ogledom in nakupom.</p>

<h3>6. Zaključek s pozivom</h3>

<p>Kratko in konkretno. »Na zalogi, odpošljemo v enem delovnem dnevu.« deluje bolje kot »Naročite zdaj!«.</p>

<h2>Jezik, ki prodaja, in jezik, ki ne</h2>

<p>Najpogostejša napaka slovenskih opisov je prazna superlativnost: vrhunska kakovost, nepozabno doživetje, edinstvena ponudba. Te besede ne povedo ničesar in jih kupci berejo kot šum.</p>

<p>Zamenjajte jih s <strong>preverljivimi podatki</strong>:</p>

<ul>
<li>namesto »zelo vzdržljivo« → »nerjaveče jeklo 304, primerno za pomivalni stroj«;</li>
<li>namesto »hitra dostava« → »oddano do 12.00, poslano isti dan«;</li>
<li>namesto »primerno za vse« → »ustreza obsegu zapestja 15–20 cm«.</li>
</ul>

<p>Pišite v drugi osebi in v sedanjiku. »Steklenico zaprete z enim gibom« je bližje kupcu kot »Zapiranje steklenice je omogočeno z enim gibom«. Izogibajte se trpniku in samostalniškim nizom, ki so v slovenskih opisih žal pogosti.</p>

<p>In najpomembneje: <strong>ne obljubljajte nič, česar ne morete izpolniti</strong>. Napačna obljuba o roku dostave ali lastnosti izdelka povzroči vračilo, slabo oceno in izgubo zaupanja — kar je dražje od prodaje, ki ste jo s to obljubo pridobili.</p>

<h2>SEO: kako naj opis najde Google</h2>

<p>Dober opis za kupca je že skoraj dober opis za Google. Nekaj stvari pa je treba narediti zavestno.</p>

<h3>Uporabite izraze, ki jih kupci res iščejo</h3>

<p>Kupci ne iščejo »termo posode za tekočine«, ampak »termovka«. Ne iščejo »obutve za tek po neurejenih površinah«, ampak »trail tekaški copati«. Preverite, kako izdelku pravi kupec, ne katalog dobavitelja. Pri tem pomagajo Googlovi predlogi med tipkanjem in razdelek »Sorodna iskanja« na dnu rezultatov.</p>

<h3>Naslov izdelka je najmočnejši element</h3>

<p>Naslov naj vsebuje vrsto izdelka, znamko, ključno lastnost in po potrebi velikost. »Termovka Rocco 0,5 l, nerjaveče jeklo, drži toploto 12 h« pove več kot »Rocco 500«.</p>

<h3>Nikoli ne prepisujte opisa dobavitelja</h3>

<p>Če ima deset trgovin isto besedilo, Google izbere eno — najpogosteje največjo. Lasten opis je najcenejša prednost, ki jo imate pred konkurenco.</p>

<h3>Poskrbite za fotografije in nadomestna besedila</h3>

<p>Opis in fotografije delujeta skupaj. Vsaka slika naj ima opisno nadomestno besedilo (alt), ki pove, kaj je na njej. Več o tem v vodniku <a href="/clanek/optimizacija-fotografij-izdelka">Optimizacija fotografij izdelka</a>.</p>

<h3>Ne pretiravajte s ključnimi besedami</h3>

<p>Ponavljanje iste besedne zveze v vsakem stavku danes ne pomaga, ampak škodi. Ključno besedo uporabite v naslovu, v prvem odstavku in enkrat ali dvakrat v besedilu — naravno, tako kot bi jo izgovorili.</p>

<h2>Pogoste napake in kako jih odpraviti</h2>

<ul>
<li><strong>Opis iz treh besed.</strong> »Kakovosten izdelek, hitra dostava.« Kupec nima česa brati, Google nima česa razumeti. Najmanj 120 do 150 besed na izdelek je realna spodnja meja.</li>
<li><strong>Enak opis za deset različic.</strong> Če se izdelki razlikujejo po velikosti ali barvi, naj se razlikujejo tudi opisi — vsaj v prvih stavkih.</li>
<li><strong>Manjkajoče mere.</strong> Najpogostejši razlog za vračilo in za vprašanje po e-pošti.</li>
<li><strong>Besedilo v sliki.</strong> Podatki na fotografiji za iskalnike ne obstajajo. Vedno jih zapišite tudi v besedilo.</li>
<li><strong>Kopiranje konkurence.</strong> Prevzamete tudi njihove napake in ostanete brez razlikovalne prednosti.</li>
<li><strong>Slovnične napake.</strong> Vplivajo na zaupanje bolj, kot večina trgovcev misli. Besedilo vedno preberite še enkrat naslednji dan.</li>
</ul>

<h2>Praktičen primer: pred in po</h2>

<p><strong>Pred:</strong> »Kakovostna lesena deska za rezanje. Vrhunska izdelava, dolga življenjska doba. Primerna za vsako kuhinjo.«</p>

<p><strong>Po:</strong> »Deska za rezanje iz masivne bukve, 38 × 25 × 2 cm. Bukev je trda in ne poškoduje rezila, zato deska ostane gladka tudi po letih uporabe. Na eni strani ima žleb za sok, ki prepreči, da bi tekočina stekla po pultu — uporabna pri rezanju mesa in sočnega sadja. Ročno brušena in obdelana z oljem za stik z živili. Pomivanje: ročno, s toplo vodo; ni primerna za pomivalni stroj. Enkrat na nekaj mesecev jo premažite z oljem in zdržala bo leta.«</p>

<p>Drugo besedilo je daljše, a vsak stavek odgovarja na resnično vprašanje. Prav to je razlika med opisom, ki zapolni prostor, in opisom, ki proda.</p>

<h2>Koliko časa vzeti za en opis</h2>

<p>Realno: 15 do 30 minut za izdelek, ki ga dobro poznate. To se zdi veliko, dokler ne izračunate, koliko prinese. Če izdelek prodajate dve leti, je pol ure vloženega dela razporejeno na stotine ogledov.</p>

<p>Priporočen vrstni red dela: začnite pri desetih izdelkih z največ ogledi. Tam vsak popravek najhitreje prinese rezultat. Šele nato se lotite ostalih. Če imate v katalogu več sto izdelkov, si določite ritem — na primer pet prenovljenih opisov na teden — namesto da se lotite vsega naenkrat in obupate.</p>

<h2>Kontrolni seznam pred objavo</h2>

<ul>
<li>Ali kupec v prvih dveh vrsticah ve, kaj izdelek je in za koga je?</li>
<li>So navedene vse mere, material in vsebina paketa?</li>
<li>Je odgovorjeno na najpogostejše vprašanje kupcev?</li>
<li>Je besedilo vaše in ne prepisano od dobavitelja?</li>
<li>Ali naslov vsebuje izraz, ki ga kupci res iščejo?</li>
<li>So alineje kratke in berljive na telefonu?</li>
<li>Ni nobene obljube, ki je ne morete izpolniti?</li>
<li>Ste besedilo prebrali na glas in odpravili okorne povedi?</li>
</ul>

<h2>Pogosta vprašanja</h2>

<p><strong>Kako dolg naj bo opis izdelka?</strong><br>
Toliko, da odgovori na vsa kupčeva vprašanja, in nič več. Za preprost izdelek zadošča 150 do 250 besed, za tehnično zahtevnejšega 400 do 600. Dolžina sama po sebi ni cilj.</p>

<p><strong>Ali lahko uporabim opis dobavitelja?</strong><br>
Lahko, a le kot vir podatkov. Besedilo prepišite s svojimi besedami in dodajte tisto, česar dobavitelj ne ve — kako se izdelek obnese pri vaših kupcih.</p>

<p><strong>Ali naj v opis zapišem ceno?</strong><br>
Ne. Cena se prikazuje ločeno in se spreminja; če jo napišete v besedilo, boste prej ali slej imeli dve različni ceni na isti strani.</p>

<p><strong>Kaj pa umetna inteligenca za pisanje opisov?</strong><br>
Kot pomoč pri osnutku je uporabna, kot končno besedilo ne. Umetna inteligenca ne pozna vaših kupcev in si podatke lahko izmisli. Vsak podatek — mere, materiale, roke — preverite sami.</p>

<p><strong>Kako pogosto naj opise posodabljam?</strong><br>
Ko se izdelek spremeni, ko se ponovi isto vprašanje kupcev ali ko ugotovite, da stran dobi obisk, a ne prodaje. Sicer zadošča pregled enkrat na leto.</p>

<h2>Zaključek</h2>

<p>Opis izdelka je eno redkih mest v spletni trgovini, kjer lahko z eno uro dela trajno izboljšate prodajo, ne da bi karkoli plačali za oglase. Napišite ga za kupca, ki stoji pred odločitvijo — s podatki, ne s pridevniki.</p>

<p>Če izdelke prodajate na kupish.si, imate dodatno prednost: glavni opis in tehnične podrobnosti sta ločena, tako da lahko poveste veliko, ne da bi stran postala nepregledna. Kako začeti, je opisano na strani <a href="/najem-spletne-trgovine">Najem spletne trgovine</a>.</p>

<p>Sorodni vodniki: <a href="/clanek/optimizacija-fotografij-izdelka">Optimizacija fotografij izdelka</a>, <a href="/clanek/kako-pridobiti-kupce-v-spletno-trgovino">Kako pridobiti kupce v spletno trgovino</a> in <a href="/clanek/siritev-prodaje-na-nove-kanale">Širitev prodaje na nove kanale</a>.</p>
`,
};
